<template>
  <EquipmentBase :section-id="ScenarioDetailSectionKey.WELL_COMPLETION">
    <EquipmentHeader>
      <template #title> Well Completion </template>
    </EquipmentHeader>
    <div class="cell mb-3">
      <div class="flex" style="align-items: center; display: flex">
        <a
          href="/spyglass_basic_data_sheet.xlsx"
          download="spyglass_basic_data_sheet.xlsx"
          class="mr-5"
        >
          <icon :icon="['far', 'link']" />
          <span class="ml-1">Spyglass Basic Data Sheet</span>
        </a>
        <FileInput
          icon="fas fa-plus-circle"
          label="Add Spyglass Basic Data Sheet"
          :auto="true"
          accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          :maxFileSize="5000000"
          :show-cancel-button="false"
          :instant-reset="true"
          @change="spyglassDataSheetFileInputChange"
        />
      </div>
    </div>
    <!-- Top Casing -->
    <CasingRow
      :casing="topCasing"
      :hide-add-above="true"
      :hide-add-below="topCasingHideAddBelowButton"
      :hide-remove="!topCasing?.id"
      @add-below="newBottomCasing = true"
      @remove="removeCasing('top')"
    />
    <!-- Bottom Casing -->
    <CasingRow
      v-if="newBottomCasing || bottomCasing?.id"
      :casing="bottomCasing"
      :top-casing="false"
      :hide-add-above="true"
      :hide-add-below="true"
      :hide-remove="false"
      @remove="removeCasing('bottom')"
    />

    <Divider />

    <DeviationSurvey />
  </EquipmentBase>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import {
  Deviation_Survey_Directional_Dataset_Enum,
  type Casings,
  type Tubings,
  useCasingsQuery,
  useTubingsQuery
} from "gql/api";
import DeviationSurvey from "@/components/deviationSurvey/DeviationSurvey.vue";
import EquipmentBase from "@/components/scenarios/EquipmentBase.vue";
import EquipmentHeader from "@/components/scenarios/EquipmentHeader.vue";
import CasingRow from "@/components/equipment/casings/CasingRow.vue";
import FileInput from "@/components/FileInput.vue";
import { injectStrict, omitUnknowns } from "@/utils/utils";
import {
  deviationSurveyFormKey,
  equipmentFormKey,
  parametersFormKey,
  scenarioCalculationsKey,
  scenarioFormKey,
  wellPropertiesFormKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { ScenarioDetailSectionKey } from "@/composables/scenarioCalculations/useEquipmentCalculatorSections";
import { parseWellDataSheet } from "@/utils/parseWellDataSheet";

import { useToast } from "primevue/usetoast";
import {
  adminCasingVars,
  defaultCasingVars,
  ecopetrolCasingVars,
  kocCasingVars
} from "@/models/equipment/casings/predicates";
import {
  adminTubingVars,
  defaultTubingVars,
  ecopetrolTubingVars,
  kocTubingVars
} from "@/models/equipment/tubings/predicates";
import useUserInfo from "@/composables/auth/useUserInfo";
import { extrapolateTVDByMD } from "@/libs/calculations/deviationSurvey";

const scenarioForm = injectStrict(scenarioFormKey);
const parametersForm = injectStrict(parametersFormKey);
const equipmentForm = injectStrict(equipmentFormKey);
const wellPropertiesForm = injectStrict(wellPropertiesFormKey);
const wellProperties = computed(() => wellPropertiesForm.data.value);
const deviationSurveyForm = injectStrict(deviationSurveyFormKey);
const { entryRows } = injectStrict(scenarioCalculationsKey);
const uploadedFile = ref();
const toaster = useToast();
const newBottomCasing = ref(false);
const user = useUserInfo();

// Lazy query setup - won't execute until called
// The casing/tubing data returned here is only used for parsing the data sheet
// so we don't use the useTubings/useCasings composables for them
const getCasingVariables = () => {
  if (user.is_koc_customer) return kocCasingVars;
  if (user.is_ecopetrol_customer) return ecopetrolCasingVars;
  if (user.is_admin) return adminCasingVars;
  return defaultCasingVars;
};

const getTubingVariables = () => {
  if (user.is_koc_customer) return kocTubingVars;
  if (user.is_ecopetrol_customer) return ecopetrolTubingVars;
  if (user.is_admin) return adminTubingVars;
  return defaultTubingVars;
};

const casingsQuery = useCasingsQuery({
  variables: getCasingVariables(),
  pause: true // Don't execute on mount
});

const tubingsQuery = useTubingsQuery({
  variables: getTubingVariables(),
  pause: true // Don't execute on mount
});

const topCasing = computed(() => wellProperties.value.r_top_casing as Casings);
const bottomCasing = computed(
  () => wellProperties.value.r_bottom_casing as Casings
);
const topCasingHideAddBelowButton = computed(() => {
  return (
    newBottomCasing.value || !!bottomCasing.value?.id || !topCasing.value?.id
  );
});
function removeCasing(casingPosition: "top" | "bottom") {
  // save bottom casing data
  const { r_bottom_casing, bottom_casing_length_feet, bottom_casing_id } =
    wellProperties.value;

  if (casingPosition === "bottom") {
    // clear bottom casing
    wellPropertiesForm.update({
      r_bottom_casing: null,
      bottom_casing_id: null,
      bottom_casing_length_feet: null
    });
  }
  // if top casing was deleted
  // move bottom casing data to top casing
  if (casingPosition === "top") {
    wellPropertiesForm.update({
      r_top_casing: r_bottom_casing,
      top_casing_id: bottom_casing_id,
      top_casing_length_feet: bottom_casing_length_feet
    });
    wellPropertiesForm.update({
      r_bottom_casing: null,
      bottom_casing_id: null,
      bottom_casing_length_feet: null
    });
  }
  newBottomCasing.value = false;
}

// Disable top of perfs tvd whenever there's a survey added
const hasDeviationSurvey = computed(() => {
  return !!(
    !deviationSurveyForm.data.value?.deleted_at &&
    deviationSurveyForm.data.value?.entry_rows
  );
});

const is3DSSurvey = computed(() => {
  return (
    hasDeviationSurvey.value &&
    wellPropertiesForm.data.value.r_deviation_survey?.directional_data_set ===
      Deviation_Survey_Directional_Dataset_Enum.ThreeDMdInclinationAzimuth
  );
});

watch(is3DSSurvey, val => {
  if (val) {
    wellPropertiesForm.update({
      average_dls: null
    });
  }
});

async function spyglassDataSheetFileInputChange(event: Event) {
  const { files } = event.target as HTMLInputElement;
  if (files) {
    const file: File = files[0];
    uploadedFile.value = file;
    if (file) {
      try {
        // Lazy load equipment data only when needed
        const { fetching: casingFetching, data: casingData } =
          casingsQuery.executeQuery();
        const { fetching: tubingFetching, data: tubingData } =
          tubingsQuery.executeQuery();

        const completed = computed(() => {
          return !casingFetching.value && !tubingFetching.value;
        });
        // Only parse file once equipment data has loaded
        if (completed.value) {
          const entry = await parseWellDataSheet(
            file,
            (casingData?.value?.casings as Casings[]) || [],
            (tubingData?.value?.tubings as Tubings[]) || []
          );

          if (entry) {
            wellPropertiesForm.update(
              omitUnknowns(entry.wellPropertiesFormData)
            );
            equipmentForm.update(omitUnknowns(entry.equipmentFormData));
            parametersForm.update(omitUnknowns(entry.parametersFormData));
            scenarioForm.update(omitUnknowns(entry.scenarioFormData));
          } else {
            toaster.add({
              severity: "error",
              life: 4000,
              summary: "Well Data Sheet Upload Error",
              detail: "Invalid data in file"
            });
          }
        }
      } catch (error: unknown) {
        if (error instanceof TypeError) {
          toaster.add({
            severity: "error",
            life: 4000,
            summary: "Well Data Sheet Upload Error",
            detail: error.message
          });
        } else {
          toaster.add({
            severity: "error",
            life: 4000,
            summary: "Well Data Sheet Upload Error",
            detail: "Something went wrong"
          });
        }
      }
    } else {
      toaster.add({
        severity: "error",
        life: 4000,
        summary: "Well Data Sheet Upload Error",
        detail: "No file selected"
      });
    }
  } else {
    toaster.add({
      severity: "error",
      life: 4000,
      summary: "Well Data Sheet Upload Error",
      detail: "No file selected"
    });
  }
}

// Update top of perfs tvd whenever top of perfs md is changed with a survey
watch(
  () => wellPropertiesForm.data.value?.top_of_perforations_measured_depth_feet,
  (val: number) => {
    if (hasDeviationSurvey.value) {
      wellPropertiesForm.update({
        top_of_perforations_true_vertical_depth_feet: extrapolateTVDByMD(
          val,
          entryRows.value
        )
      });
    }
  }
);
</script>
